/**
 * The client e-mail header, previewed exactly as the backend renders it.
 *
 * A line-for-line port of Planny.Common/Utilities/OklchColor.cs and
 * HeaderPaletteUtility.cs: the e-mails move a studio's two background colours into a
 * lightness band where white or ink text clears WCAG AA, in OKLCH so hue and chroma
 * stay where the studio put them. Keep the two in step; the tests mirror
 * HeaderPaletteUtilityTests.cs.
 */

export interface IEmailHeaderPalette {
  gradientFrom: string;
  gradientTo: string;
  textColor: string;
  pillBackground: string;
  pillBorder: string;
}

export interface IOklch {
  l: number;
  c: number;
  h: number;
}

export const EMAIL_LIGHT_TEXT = "#ffffff";
export const EMAIL_DARK_TEXT = "#15102b";

const TARGET_CONTRAST = 4.5;
const DARK_BAND_MAX_LIGHTNESS = 0.54;
const LIGHT_BAND_MIN_LIGHTNESS = 0.64;

const WHITE_PILL = "rgba(255,255,255,0.16)";
const WHITE_PILL_BORDER = "rgba(255,255,255,0.24)";
const INK_PILL = "rgba(21,16,43,0.08)";
const INK_PILL_BORDER = "rgba(21,16,43,0.16)";

/** Parses "#rgb", "#rrggbb" and the bare "rrggbb" studios store. */
export const parseHex = (
  hex: string | null | undefined,
): [number, number, number] | null => {
  if (!hex || !hex.trim()) {
    return null;
  }

  let value = hex.trim().replace(/^#+/, "");

  if (value.length === 3) {
    value = value
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }

  if (value.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(value)) {
    return null;
  }

  const packed = parseInt(value, 16);
  return [(packed >> 16) & 0xff, (packed >> 8) & 0xff, packed & 0xff];
};

/** Lower-cased, '#'-prefixed, three-digit shorthand expanded. */
export const normaliseHex = (hex: string): string => {
  let value = hex.trim().replace(/^#+/, "");

  if (value.length === 3) {
    value = value
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }

  return `#${value.toLowerCase()}`;
};

const toLinear = (channel: number) => {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

// C#'s Math.Round rounds half to even; Math.round would not.
const roundHalfEven = (value: number) => {
  const floor = Math.floor(value);
  const diff = value - floor;

  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
};

const toByte = (linear: number) => {
  const clamped = Math.min(1, Math.max(0, linear));
  const srgb =
    clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * clamped ** (1 / 2.4) - 0.055;

  return Math.min(255, Math.max(0, roundHalfEven(srgb * 255)));
};

const cube = (v: number) => v * v * v;

const toLinearRgb = (l: number, c: number, h: number): [number, number, number] => {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);

  const lCube = cube(l + 0.3963377774 * a + 0.2158037573 * b);
  const mCube = cube(l - 0.1055613458 * a - 0.0638541728 * b);
  const sCube = cube(l - 0.0894841775 * a - 1.291485548 * b);

  return [
    4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
    -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
    -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
  ];
};

const isInGamut = (l: number, c: number, h: number) => {
  const epsilon = 1e-4;
  return toLinearRgb(l, c, h).every((v) => v >= -epsilon && v <= 1 + epsilon);
};

export const oklchFromHex = (hex: string | null | undefined): IOklch | null => {
  const rgb = parseHex(hex);

  if (!rgb) {
    return null;
  }

  const [lr, lg, lb] = rgb.map(toLinear);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  return {
    l: okL,
    c: Math.sqrt(okA * okA + okB * okB),
    h: ((Math.atan2(okB, okA) * 180) / Math.PI + 360) % 360,
  };
};

/** Back to "#rrggbb", reducing chroma until the colour fits sRGB. */
export const oklchToHex = ({ l, c, h }: IOklch): string => {
  let chroma = c;

  if (!isInGamut(l, chroma, h)) {
    let lo = 0;
    let hi = chroma;

    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      if (isInGamut(l, mid, h)) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    chroma = lo;
  }

  return `#${toLinearRgb(l, chroma, h)
    .map((v) => toByte(v).toString(16).padStart(2, "0"))
    .join("")}`;
};

export const emailRelativeLuminance = (hex: string | null | undefined): number | null => {
  const rgb = parseHex(hex);

  if (!rgb) {
    return null;
  }

  const [r, g, b] = rgb.map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastOfLuminances = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const meetsTarget = (l: number, c: number, h: number, textLuminance: number) => {
  const luminance = emailRelativeLuminance(oklchToHex({ l, c, h }));
  return (
    luminance !== null && contrastOfLuminances(luminance, textLuminance) >= TARGET_CONTRAST
  );
};

const pushUntilLegible = (
  original: string,
  color: IOklch,
  text: string,
  darker: boolean,
): string => {
  const textLuminance = emailRelativeLuminance(text);

  if (textLuminance === null || meetsTarget(color.l, color.c, color.h, textLuminance)) {
    return normaliseHex(original);
  }

  let lo = darker ? 0 : color.l;
  let hi = darker ? color.l : 1;

  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const passes = meetsTarget(mid, color.c, color.h, textLuminance);

    if (passes === darker) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return oklchToHex({ l: darker ? lo : hi, c: color.c, h: color.h });
};

/** HeaderPaletteUtility.Resolve(primary, secondary). */
export const resolveEmailHeaderPalette = (
  primary: string | null | undefined,
  secondary: string | null | undefined,
): IEmailHeaderPalette => {
  let from = !primary || !primary.trim() ? "" : primary;
  let to = !secondary || !secondary.trim() ? from : secondary;

  let a = oklchFromHex(from);
  let b = oklchFromHex(to);

  if (!a && !b) {
    return {
      gradientFrom: from,
      gradientTo: to,
      textColor: EMAIL_LIGHT_TEXT,
      pillBackground: WHITE_PILL,
      pillBorder: WHITE_PILL_BORDER,
    };
  }

  // One bad stop must not cost the other its guarantee: treat the header as flat.
  if (!a) {
    from = to;
    a = b;
  }
  if (!b) {
    to = from;
    b = a;
  }

  const costOfGoingDark =
    Math.max(0, a!.l - DARK_BAND_MAX_LIGHTNESS) + Math.max(0, b!.l - DARK_BAND_MAX_LIGHTNESS);
  const costOfGoingLight =
    Math.max(0, LIGHT_BAND_MIN_LIGHTNESS - a!.l) + Math.max(0, LIGHT_BAND_MIN_LIGHTNESS - b!.l);

  const goDark = costOfGoingDark <= costOfGoingLight;
  const text = goDark ? EMAIL_LIGHT_TEXT : EMAIL_DARK_TEXT;

  return {
    gradientFrom: pushUntilLegible(from, a!, text, goDark),
    gradientTo: pushUntilLegible(to, b!, text, goDark),
    textColor: text,
    pillBackground: goDark ? WHITE_PILL : INK_PILL,
    pillBorder: goDark ? WHITE_PILL_BORDER : INK_PILL_BORDER,
  };
};
